// Module 06 — finance layer: a tamper-EVIDENT, append-only SHA-256 hash-chain ledger.
// HONEST framing: this is NOT a blockchain — single writer, no consensus, no mining, no
// distribution; `confirmations` is a display depth, not proof-of-work; tamper-evident ≠ tamper-proof.
// Hashing uses the Web Platform `crypto.subtle.digest` (local compute, async) — no `crypto-js`.
// Payments flow UPSTREAM: consumer (energy Trade.to) pays supplier (energy Trade.from).
// Boundary: imports only domain + same-module siblings (flow). Does NOT import the comms layer.
//
// ── P14 (C65/C116/C63) DISPOSITION — OFFLINE AUDUIT UTILITY, NOT ON THE HOT TICK PATH ──
// The live per-tick money identity `|Σpay − Σreceive| < 1e-6` is enforced by the powerflow invariant
// harness (src/powerflow/invariants.ts assertion family #6 "money", fed by engine/audit-assembler.ts),
// which runs synchronously every tick over the drained settlement events. This SHA-256 chain
// (`settle`/`verifyLedger`/`genesisLedger`) is an OFFLINE, async (`crypto.subtle`) tamper-evident
// audit primitive retained for future offline reconciliation / export — it is NEVER called from the
// engine tick (the tick uses the scalar `cumSettlementDollars` accumulator + the live invariant).
// C63 ("two prices for one P2P trade") is moot in the live path: the tick's `trade` events use the
// single locational `tr.pricePerKwh`; only this OFFLINE `settle()` re-prices at flat `prosumerSellPerKwh`,
// and it is not on the hot path.
import type { NodeId, PriceSample, FlowSample } from "../domain/index";
import { pairEdgeId } from "./flow";

const KWH_PER_MWH = 1000;
export const GENESIS_HASH = "0".repeat(64);

// `Trade` is a STRUCTURAL twin of economics.Trade — we accept its shape as a value, but never import
// across the module boundary (CLAUDE.md #2). Engine (07) passes economics Trade[] here; structural
// typing makes it assignable.
export interface Trade {
  from: NodeId; // supplier (energy exporter)
  to: NodeId; // consumer (energy importer)
  mwh: number;
  pricePerKwh: number;
}

export interface LedgerEntry {
  from: NodeId; // payer (consumer)
  to: NodeId; // payee (supplier)
  amount: number; // $
  // settlement-chain attribution (build log 45, additive): which leg of the bill this entry settles —
  // commodity→generators, transmission→transmitters, distribution→LDC, p2p→neighbour trade. Optional so
  // existing callers/fixtures are unaffected; the hash payload (blockPayload) ignores it for back-compat.
  kind?: "commodity" | "transmission" | "distribution" | "p2p";
}
export interface LedgerBlock {
  index: number;
  prevHash: string;
  hash: string;
  entries: readonly LedgerEntry[];
}
export interface Ledger {
  balances: ReadonlyMap<NodeId, number>; // net $ position per node
  confirmations: number; // = blocks.length (display depth, NOT PoW)
  blocks: readonly LedgerBlock[];
  headHash: string; // hash of the last block; genesis 64×'0'
}

// Recursively key-sorted JSON so two logically-equal payloads hash identically regardless of
// insertion order (JSON.stringify does NOT sort keys). Arrays keep order; numbers via JSON.stringify.
export function canonicalStringify(v: unknown): string {
  if (v === null || typeof v !== "object") return JSON.stringify(v);
  if (Array.isArray(v)) return `[${v.map(canonicalStringify).join(",")}]`;
  const obj = v as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalStringify(obj[k])}`).join(",")}}`;
}

async function sha256Hex(s: string): Promise<string> {
  const bytes = new TextEncoder().encode(s);
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
}

// The exact payload that is hashed for a block (fixed, shared by settle + verify).
function blockPayload(index: number, prevHash: string, entries: readonly LedgerEntry[]): unknown {
  return {
    index,
    prevHash,
    entries: entries.map((e) => ({ from: e.from, to: e.to, amount: e.amount })),
  };
}

export function genesisLedger(): Ledger {
  return { balances: new Map(), confirmations: 0, blocks: [], headHash: GENESIS_HASH };
}

export async function settle(
  ledger: Ledger, trades: Trade[], price: PriceSample,
): Promise<{ ledger: Ledger; flows: FlowSample[] }> {
  const entries: LedgerEntry[] = trades.map((t) => ({
    from: t.to, // payer = consumer
    to: t.from, // payee = supplier (neighbour)
    amount: t.mwh * KWH_PER_MWH * price.prosumerSellPerKwh,
    kind: "p2p", // these settle the peer-to-peer trades; retail legs are tagged at the engine
  }));

  const index = ledger.blocks.length;
  const prevHash = ledger.headHash;
  const hash = await sha256Hex(canonicalStringify(blockPayload(index, prevHash, entries)));
  const block: LedgerBlock = { index, prevHash, hash, entries };

  const balances = new Map(ledger.balances);
  for (const e of entries) {
    balances.set(e.from, (balances.get(e.from) ?? 0) - e.amount);
    balances.set(e.to, (balances.get(e.to) ?? 0) + e.amount);
  }

  const flows: FlowSample[] = entries.map((e) => ({
    edgeId: pairEdgeId(e.from, e.to), // consumer → supplier (upstream payment)
    layer: "finance",
    magnitude: e.amount,
    direction: 1,
  }));

  return {
    ledger: { balances, confirmations: index + 1, blocks: [...ledger.blocks, block], headHash: hash },
    flows,
  };
}

// Recompute the chain from scratch: each block's stored hash must match a re-hash of its payload, and
// each prevHash must link to the previous block. Any mutated past entry breaks the re-hash ⇒ false.
export async function verifyLedger(ledger: Ledger): Promise<boolean> {
  let prev = GENESIS_HASH;
  for (const block of ledger.blocks) {
    if (block.prevHash !== prev) return false;
    const recomputed = await sha256Hex(canonicalStringify(blockPayload(block.index, block.prevHash, block.entries)));
    if (recomputed !== block.hash) return false;
    prev = block.hash;
  }
  return prev === ledger.headHash;
}
